/**
 * AffiliateVerificationCode — second step of the portal's password reset.
 *
 * Reached only from AffiliateForgotPassword, which carries the email and the
 * reset token in router state; without them there is nothing to verify, so the
 * visitor goes back to the start of the flow.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import { isInvalidOtpError } from '@wlb/shared/api/auth/authErrors';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { FormError } from '../components/ui/FormError';
import { OtpInput } from '../features/auth/OtpInput';
import { AffiliatePortalLayout } from '../features/affiliate/AffiliatePortalLayout';
import type { AffiliatePasswordResetState } from '../features/affiliate/affiliateFlowState';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';
import '../components/layout/authFlow.css';
// `otp-input*` and `auth-flow__link-button` (the resend control) live here.
import '../features/auth/auth.css';
import '../features/affiliate/affiliatePortal.css';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

export function AffiliateVerificationCode() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as AffiliatePasswordResetState | null;
  const email = state?.email ?? '';

  const [passwordResetToken, setPasswordResetToken] = useState(
    state?.passwordResetToken ?? '',
  );
  const [otpCode, setOtpCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [formError, setFormError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // `onComplete` and the submit button can both fire for the same code.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown(seconds => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  if (!email || !passwordResetToken) {
    return <Navigate to={AFFILIATE_ROUTES.FORGOT_PASSWORD} replace />;
  }

  const verify = async (code: string) => {
    if (submittingRef.current) return;
    if (code.length < OTP_LENGTH) {
      setCodeError(t('auth.verificationCode.codeIncomplete', { length: OTP_LENGTH }));
      return;
    }
    setCodeError('');
    setFormError('');
    submittingRef.current = true;
    setVerifying(true);
    try {
      await affiliateService.verifyPasswordResetOtp({ passwordResetToken, otpCode: code });
      const next: AffiliatePasswordResetState = {
        email,
        passwordResetToken,
        otpCode: code,
      };
      navigate(AFFILIATE_ROUTES.RESET_PASSWORD, { state: next });
    } catch (error) {
      if (isInvalidOtpError(error)) {
        setCodeError(t('auth.verificationCode.invalidCode'));
        setOtpCode('');
      } else {
        setFormError(
          getUserFacingErrorMessage(error, t('affiliate.forgotPassword.verifyFailed')),
        );
      }
    } finally {
      submittingRef.current = false;
      setVerifying(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    verify(otpCode).catch(() => undefined);
  };

  const handleResend = async () => {
    setResending(true);
    setFormError('');
    try {
      // Each request issues a fresh token, so the newest one replaces the one
      // this screen arrived with.
      const reset = await affiliateService.forgotPassword(email);
      setPasswordResetToken(reset.passwordResetToken);
      setOtpCode('');
      setCodeError('');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      ToastService.show({
        type: 'success',
        message: t('auth.verificationCode.codeResent'),
      });
    } catch (error) {
      setFormError(
        getUserFacingErrorMessage(error, t('affiliate.forgotPassword.failed')),
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <AffiliatePortalLayout centered>
      <div className="auth-flow__card">
        <button
          className="auth-flow__back"
          type="button"
          onClick={() => navigate(AFFILIATE_ROUTES.FORGOT_PASSWORD)}
        >
          ‹ {t('common.buttons.back')}
        </button>
        <h1 className="auth-flow__heading">{t('auth.verificationCode.heading')}</h1>
        <p className="auth-flow__subheading">
          {t('auth.verificationCode.subheadingPrefix')} {email}{' '}
          {t('auth.verificationCode.subheadingSuffix')}
        </p>

        <form className="auth-flow__form" onSubmit={handleSubmit} noValidate>
          <FormError
            variant="banner"
            message={formError}
            id="affiliate-verification-form-error"
          />
          <div className="auth-flow__field">
            <OtpInput
              value={otpCode}
              length={OTP_LENGTH}
              onChange={setOtpCode}
              onComplete={code => {
                verify(code).catch(() => undefined);
              }}
              disabled={verifying}
              invalid={Boolean(codeError)}
              describedBy={codeError ? 'affiliate-verification-code-error' : undefined}
            />
            <FormError message={codeError} id="affiliate-verification-code-error" />
          </div>
          <button
            className="auth-flow__primary"
            type="submit"
            disabled={verifying || otpCode.length < OTP_LENGTH}
          >
            {verifying ? t('common.loading') : t('auth.verificationCode.continue')}
          </button>
        </form>

        <p className="auth-flow__footer">
          <button
            className="auth-flow__link-button"
            type="button"
            onClick={() => {
              handleResend().catch(() => undefined);
            }}
            disabled={resending || cooldown > 0}
          >
            {cooldown > 0
              ? t('auth.verificationCode.resendIn', { seconds: cooldown })
              : t('auth.verificationCode.resendCode')}
          </button>
        </p>
      </div>
    </AffiliatePortalLayout>
  );
}
